import { unauthorizedWsCallsApiConnect } from '@gem/call/WsApi/wsApi';
import { logger } from '@gem/modules/logger';

import Tasks from 'APP/Tasks';
import { unauthorizedLogin as apiLogin } from 'APP/model/auth/authModel';
import { unauthorizedRefreshToken as apiRefreshToken } from 'APP/model/auth/authModel';
import { IUnauthorizedLoginSuccess } from 'APP/model/auth/authModel.types';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { storage } from 'APP/packages/storage';
import { setSseConfig } from 'APP/packages/streamApi';
import { initUnauthorizedAuthClient } from 'APP/restApi/rest/authorization';
import { setWebsocketConfig } from 'APP/wsApi/wsApi.config';
import Entities from 'STORE';

import { IRefreshTokenResult } from './login.types';
import { ILoginResult } from './login.types';

export async function unauthorizedLogin(selfName = '', channelId = ''): Promise<ILoginResult> {
  try {
    const deeplinkUrl = storage.guestCall.getGuestCallsInvitationUrl();

    if (deeplinkUrl) {
      await Tasks.config.updateUnauthorizedHostConfig(deeplinkUrl);
    } else {
      throw new Error('UnauthorizedLogin. deeplinkUrl is undefined');
    }
    initUnauthorizedAuthClient(ProjectUrls.unauthorizedUrl);
    const response: IUnauthorizedLoginSuccess = await apiLogin({
      username: selfName,
      channelId: channelId,
      deviceId: '-1',
    });

    if (!response.accessToken) {
      return { isSuccess: false, error: new Error('401 Unauthorized') };
    }

    await initUnauthorizedProject(response, channelId, response.accessToken);

    return { isSuccess: true };
  } catch (error) {
    logger.get('auth').error('login', error);
    return { isSuccess: false, error };
  }
}
export async function initUnauthorizedProject(
  loginResponse: IUnauthorizedLoginSuccess,
  channelId: string,
  token = loginResponse.accessToken
): Promise<boolean> {
  storage.refreshToken.set(loginResponse.refreshToken);
  setWebsocketConfig({ token });
  setSseConfig({ token });

  unauthorizedWsCallsApiConnect({
    session: loginResponse.accessToken,
    unauthorizedWsCallsUrl: ProjectUrls.unauthorizedUrl,
    channelId,
    refreshToken: async () => {
      const result = await unauthorizedRefreshToken(loginResponse.accessToken, channelId);

      if (result.isSuccess && result.token) {
        return result.token;
      } else {
        return Tasks.authorization.logout().then(() => null);
      }
    },
  });

  Entities.authStore.setSession(loginResponse.accessToken);
  Entities.authStore.setStreamToken(token);
  Entities.authStore.setIsGuestCallSession(true);

  return true;
}

let refreshTokenPromise: Promise<IRefreshTokenResult> | null = null;

export async function refreshTokenFunc(
  refreshToken: string,
  channelId: string
): Promise<IRefreshTokenResult> {
  if (!refreshToken) {
    return { isSuccess: false, token: null };
  }

  try {
    const response: IUnauthorizedLoginSuccess = await apiRefreshToken({
      refreshToken,
    });

    if (!response.accessToken || !response.refreshToken) {
      return { isSuccess: false, token: null, error: new Error('401 Unauthorized') };
    }

    //TODO: change to await initProject(response) when will move to real b2b server
    await initUnauthorizedProject(
      {
        refreshToken: response.refreshToken,
        accessToken: response.accessToken,
      },
      channelId
    );

    return { isSuccess: true, token: response.accessToken };
  } catch (error) {
    logger.get('auth').error('refreshToken', error);
    return { isSuccess: false, token: null, error };
  }
}

export async function unauthorizedRefreshToken(
  refreshToken: string,
  channelId: string
): Promise<IRefreshTokenResult> {
  if (refreshTokenPromise) {
    const result = await refreshTokenPromise;
    refreshTokenPromise = null;

    return result;
  }

  refreshTokenPromise = refreshTokenFunc(refreshToken, channelId);

  const result = await refreshTokenPromise;
  refreshTokenPromise = null;

  return result;
}
